import { useEffect, useRef, useState } from "react";
import { HomeLink } from "./HomeLink";
import { HomeAppStoreBadge } from "./HomeAppStoreBadge";

/**
 * Mobile sticky action bar.
 *
 * The page runs to roughly 7,000px on a phone and the closing action sits at the
 * very bottom, so a reader who stops halfway has nothing to act on. This follows
 * them instead.
 *
 * Deliberately does NOT fight the CTAs it duplicates:
 *  - hidden until the hero's own buttons have scrolled out of view, so the fold
 *    is never showing two competing copies of the same action
 *  - hidden again once the closing CTA is on screen, for the same reason
 *  - phone only (`md:hidden`), where the length problem actually exists
 *
 * It cannot cover content: the bar is fixed, and the page reserves matching
 * space for it (see the spacer in Home.tsx) so the footer stays reachable.
 */
export function HomeStickyCta() {
  const [visible, setVisible] = useState(false);
  const heroPassed = useRef(false);
  const closingVisible = useRef(false);

  useEffect(() => {
    const hero = document.querySelector("[data-hero-cta]");
    const closing = document.querySelector("[data-closing-cta]");
    if (!hero || !closing) return;

    const sync = () => setVisible(heroPassed.current && !closingVisible.current);

    const heroObs = new IntersectionObserver(
      ([e]) => {
        heroPassed.current = !e.isIntersecting && e.boundingClientRect.top < 0;
        sync();
      },
      { threshold: 0 },
    );
    const closingObs = new IntersectionObserver(
      ([e]) => {
        closingVisible.current = e.isIntersecting;
        sync();
      },
      { threshold: 0 },
    );
    heroObs.observe(hero);
    closingObs.observe(closing);
    return () => {
      heroObs.disconnect();
      closingObs.disconnect();
    };
  }, []);

  return (
    <div
      data-sticky-cta
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur transition-transform duration-200 md:hidden ${
        visible ? "translate-y-0" : "pointer-events-none translate-y-full"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3">
        <HomeAppStoreBadge height={44} className="-ml-[4px] shrink-0" />
        <HomeLink
          to="/browse"
          tabIndex={visible ? undefined : -1}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground"
        >
          Browse cafés
        </HomeLink>
      </div>
    </div>
  );
}
